import React, { useEffect, useState } from "react";
import { Box, Button, MenuItem, TableCell, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GenericDataTable from "./GenericDataTable";
import CompanyTableCell from "./CompanyTableCell";

const destinations = { leads: ["leads", "leadId"], companies: ["companies", "companyId"], contacts: ["contacts", "contactId"], candidates: ["candidates", "candidateId"] };
const columns = [{ id: "name", label: "Name", minWidth: 220 }, { id: "company_name", label: "Company Name", minWidth: 220 }, { id: "status", label: "Status", minWidth: 140 }];
const companyColumns = [{ id: "name", label: "Company Name", minWidth: 220 }, columns[2]];

export default function TeamMemberRecords({ memberId, view }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", records: {} });
  const [retry, setRetry] = useState(0);
  const [pipelineType, setPipelineType] = useState("candidates");
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, error: "", records: {} });
    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
    fetch(`${(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "")}/api/team-members/${encodeURIComponent(memberId)}/records`, {
      credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: controller.signal,
    }).then(async response => {
      const data = await response.json().catch(() => {
        throw new Error("The team records service is unavailable. Please retry after the server is updated.");
      });
      if (!response.ok) throw new Error(data.error || "Unable to load records");
      if (!controller.signal.aborted) setState({ loading: false, error: "", records: data });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ loading: false, error: error.message, records: {} });
    });
    return () => controller.abort();
  }, [memberId, retry]);
  const type = view === "pipeline" ? pipelineType : view;
  const rows = state.records[type] || [];
  const open = row => {
    if (type === "contacts") { navigate(`/contacts/${encodeURIComponent(row.id)}`); return; }
    const [path, param] = destinations[type];
    navigate(`/${path}?${param}=${encodeURIComponent(row.id)}&tab=details`);
  };
  if (state.loading) return <Typography role="status" sx={{ p: 3 }}>Loading assigned records…</Typography>;
  if (state.error) return <Box role="alert" sx={{ p: 3 }}><Typography color="error">{state.error}</Typography><Button onClick={() => setRetry(value => value + 1)}>Retry</Button></Box>;
  const stages = rows.reduce((result, row) => {
    const stage = row.status || "No status";
    (result[stage] ||= []).push(row);
    return result;
  }, {});
  return <Box sx={{ py: 2 }}>
    {view === "pipeline" ? <>
      <TextField select label="Pipeline" value={pipelineType} onChange={event => setPipelineType(event.target.value)} size="small" sx={{ minWidth: 200, mb: 2 }}>
        {Object.keys(destinations).map(value => <MenuItem key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</MenuItem>)}
      </TextField>
      {!rows.length && <Typography sx={{ p: 2 }}>No assigned records in this pipeline.</Typography>}
      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
        {Object.entries(stages).map(([stage, items]) => <Box key={stage} sx={{ minWidth: 240, maxWidth: 300, flex: "1 0 240px", bgcolor: "#F8FAFC", borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>{stage} ({items.length})</Typography>
          {items.map(row => <Box key={row.id} sx={{ bgcolor: "white", border: "1px solid #EAECF0", borderRadius: 2, p: 1.5, mb: 1 }}><Button onClick={() => open(row)} sx={{ textAlign: "left", textTransform: "none" }}>{row.name || "Unnamed record"}</Button><Typography variant="body2">{row.company_name}</Typography></Box>)}
        </Box>)}
      </Box>
    </> : <GenericDataTable key={view} columns={view === "companies" ? companyColumns : columns} rows={rows} includeSelectionColumn={false} defaultOrderBy="name" initialRowsPerPage={10} visualVariant="reference" renderRow={row => <>
      <TableCell>{view === "companies" ? <CompanyTableCell row={row} companyEntity onClick={() => open(row)} /> : <Button onClick={() => open(row)} sx={{ textTransform: "none", textAlign: "left" }}>{row.name || "Unnamed record"}</Button>}</TableCell>
      {view !== "companies" && <TableCell><CompanyTableCell row={row} /></TableCell>}<TableCell>{row.status || "—"}</TableCell>
    </>} />}
  </Box>;
}
